// constants.ts - constantes métier Hop Resto (règles de la spec §3).
import type { GroupSize, Service } from './types';

/** Fuseau de référence pour le « soir », la purge minuit et les dates du jour. */
export const APP_TIMEZONE = 'Europe/Paris';

/**
 * Une table disparaît 5 minutes après l'heure à laquelle elle est disponible
 * (docs/rules/table-perdue.md - amendement v1.4, remplace les 30 min initiales).
 * expiration = max(published_at, heure_du_jour) + EXPIRY_MINUTES. Passée cette
 * expiration, `expireOverdueTables` la retire automatiquement (push « plus
 * disponible » + comptage « perdue » via onTableRetiree).
 */
export const EXPIRY_MINUTES = 5;

/**
 * Longueur maximale du commentaire libre d'une demande de réservation
 * (docs/rules/commentaire-demande.md). Doit rester alignée avec la validation
 * des règles Firestore (`firestore.rules`, création de `demandes`).
 */
export const COMMENTAIRE_MAX_LENGTH = 250;

// ── Module Calendrier (docs/rules/calendrier.md) ────────────────────────────

/** Horizon de réservation future : de demain à aujourd'hui + N jours. */
export const CALENDRIER_HORIZON_JOURS = 30;

/**
 * Horizons de réservation proposés au restaurateur (Paramètres → Calendrier).
 * `CALENDRIER_HORIZON_JOURS` reste le défaut ; voir `horizonJours()`.
 */
export const CALENDRIER_HORIZON_CHOIX = [15, 30, 60] as const;

/**
 * Anti-spam : nombre maximal de demandes futures en_attente simultanées pour
 * un même téléphone (tous restos confondus). Relevé de 3 à 5 le 24 juillet
 * 2026 (3 bloquait des clients légitimes réservant plusieurs dates).
 */
export const CALENDRIER_MAX_EN_ATTENTE_PAR_TEL = 5;

/**
 * Clôture des demandes pour DEMAIN, en minutes depuis minuit (23h30 - heure du
 * cron prepareCalendrierVeille) : après cet instant, les demandes pour demain
 * sont refusées à la création ET à la validation (la bascule de la veille est
 * en cours ou passée).
 */
export const CALENDRIER_CLOTURE_MINUTES = 23 * 60 + 30;

/**
 * Taille maximale par défaut d'une table GÉNÉRÉE depuis le pool de couverts
 * flexibles (quand `couverts_max_par_table` n'est pas renseigné dans le
 * template - docs/rules/calendrier.md).
 */
export const COUVERTS_MAX_PAR_TABLE_DEFAUT = 6;

/**
 * Nombre de COUPES par défaut du pool de couverts flexibles (quand
 * `coupes_max` n'est pas renseigné) : chaque coupe divise le pool en un
 * morceau de plus (N coupes → au plus N+1 tables issues du pool). Défaut 1 :
 * un pool se divise en 2 tables au plus — décision produit du 21 juillet 2026
 * (docs/rules/calendrier.md, « jamais de miettes »).
 */
export const COUPES_MAX_DEFAUT = 1;

/**
 * Longueur maximale du message optionnel du restaurateur à la validation
 * (`reponse_resto`) - alignée sur la validation des rules.
 */
export const REPONSE_RESTO_MAX_LENGTH = 250;

/**
 * ZONES de salle (docs/rules/zones.md) - palette de couleurs proposées.
 * JAMAIS de vert (réservé à la disponibilité) ni de corail (marque/action) -
 * DA du projet. Indigo, ambre, bleu ciel, violet, rose, orange, ardoise,
 * brun.
 */
export const ZONES_COULEURS = [
  '#4F46E5',
  '#F59E0B',
  '#0EA5E9',
  '#8B5CF6',
  '#EC4899',
  '#F97316',
  '#64748B',
  '#92400E',
] as const;

/** Nombre maximal de zones par resto (onglets « Aujourd'hui » lisibles). */
export const ZONES_MAX = 8;

/** Longueur maximale du nom d'une zone (et du libellé de type « autre »). */
export const ZONE_NOM_MAX = 24;

/**
 * Heure (Europe/Paris) à laquelle partent les RAPPELS AUTOMATIQUES de la
 * veille pour les réservations du lendemain (`calendrier.rappel_veille` -
 * fonction planifiée `sendRappelsVeille`, docs/rules/rappel-resa.md).
 */
export const RAPPEL_VEILLE_HEURE = 18;

/**
 * Contacts RÉELS du support Hop Resto, affichés aux restaurateurs (PWA,
 * onglet Paramètres → Aide) - renseignés le 18 août 2026.
 */
export const SUPPORT_EMAIL = 'hop-resto@outlook.fr';
export const SUPPORT_TEL = '07 64 09 53 88';

/**
 * Fiches de l'app cliente sur les stores - PUBLIÉE le 21 septembre 2026.
 *
 * Source UNIQUE pour toutes les surfaces publiques (landing, page web
 * restaurateur `/r/{id}` et son widget embarqué) : deux copies d'une URL
 * finissent toujours par diverger, comme pour `SUPPORT_EMAIL`.
 *
 * Les liens sont donnés SANS paramètre `utm_*` : la fiche partagée par la
 * console Play en porte un par défaut (`utm_source=emea_Med`) qui ne décrit
 * aucune de nos surfaces et fausserait l'attribution. Pour mesurer d'où
 * viennent les installations, ajouter un `utm_source` PAR surface au point
 * d'appel, pas ici.
 */
export const APP_STORE_URL = 'https://apps.apple.com/fr/app/hop-resto/id6808267948';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=fr.hopresto.hop_resto';

/**
 * Nom de paquet Android de l'app cliente. Sert à construire le lien Play
 * ci-dessus ET le lien `intent://` du widget (docs/specs/spec-widget-dispos.md) :
 * avec `package=`, Chrome sait exactement quelle app ouvrir.
 */
export const ANDROID_PACKAGE = 'fr.hopresto.hop_resto';

/** Début de la COUPURE de nuit des push (spec §3.3 / §4.4) : 23h Paris. */
export const PUSH_CUTOFF_HOUR = 23;

/**
 * REPRISE des push le matin (1er septembre 2026) : la coupure court désormais
 * de 23h à 7h, au lieu de s'arrêter à minuit.
 *
 * Motif : la bascule nocturne (applyPlanningMidnight, 00h05) publie les
 * tables du jour qui commence - planning de la veille ET matérialisation du
 * calendrier. Avec une coupure s'arrêtant à minuit, ces tables poussaient une
 * notification de disponibilité à ~00h05, en pleine nuit, pour un service qui
 * n'ouvre que le midi. Personne ne cherche une table à cette heure-là.
 *
 * 7h et pas 8h : une table publiée à la main tôt le matin pour le service du
 * midi doit, elle, continuer de prévenir les chercheurs.
 * Voir docs/rules/push-notifications.md.
 */
export const PUSH_REPRISE_HOUR = 7;

/**
 * Une heure (0-23, fuseau Paris) tombe-t-elle dans la coupure de nuit ?
 * Prédicat PUR - la seule définition de la fenêtre, partagée par les Cloud
 * Functions (isPushCutoff) et testable heure par heure.
 */
export function estHeureCoupurePush(heureParis: number): boolean {
  return heureParis >= PUSH_CUTOFF_HOUR || heureParis < PUSH_REPRISE_HOUR;
}

/** Tailles de groupe proposées par le filtre et déclinées en topics FCM. */
export const GROUP_SIZES: GroupSize[] = [2, 4, 6];

/** Service par défaut (rétro-compat ; depuis v1.2 le service est déduit de l'heure). */
export const SERVICE_DEFAULT: Service = 'soir';

/**
 * Frontière midi/soir (spec §3.6) : heure < 15h00 → midi, ≥ 15h00 → soir.
 * Le restaurateur ne choisit jamais un service : il est déduit de l'heure.
 */
export const MIDI_CUTOFF_MINUTES = 15 * 60;

/** Créneaux suggérés du service midi (chips « Ce midi », maquette v1.2). */
export const HEURES_MIDI = [
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
] as const;

/** Créneaux suggérés du service soir (chips « Ce soir », maquette v1.2). */
export const HEURES_SOIR = [
  '19:00',
  '19:30',
  '20:00',
  '20:30',
  '21:00',
  '21:30',
  '22:00',
  '22:30',
  '23:00',
] as const;

/**
 * Annulation d'une résa confirmée (docs/rules/annulation-demande.md) : si le
 * client annule au moins GRACE minutes AVANT l'heure de la table, elle est
 * republiée à la MÊME heure ; plus tard, elle est recalée au prochain créneau
 * du service (ou déclarée perdue si le service est terminé).
 */
export const ANNULATION_REPUBLISH_GRACE_MINUTES = 15;

/** Valeur « heure » signifiant « dès maintenant » côté UI restaurateur. */
export const HEURE_NOW = 'maintenant';

/**
 * Durée de conservation de l'historique NOMINAL des réservations, en ANNÉES
 * calendaires (docs/rules/historique-reservations.md). Passé ce délai, les
 * coordonnées (prénom, nom, téléphone) sont effacées et seule la ligne
 * quantitative subsiste - date, heure, couverts, statut.
 */
export const RESERVATION_RETENTION_ANNEES = 3;

/**
 * Prévisions (docs/rules/previsions-ia.md) : horizon photographié chaque nuit
 * (J+1 → J+N) et rétention des photos - assez pour construire les courbes de
 * dynamique par jour de semaine, borné pour que la collection ne grossisse
 * pas sans fin.
 */
export const PREVISION_SNAPSHOT_HORIZON_JOURS = 14;
export const PREVISION_SNAPSHOT_RETENTION_JOURS = 180;

/**
 * Longueur maximale de la NOTE PRIVÉE du restaurateur sur une réservation
 * (docs/rules/note-resto.md) - alignée sur la borne des règles Firestore.
 */
export const NOTE_RESTO_MAX_LENGTH = 300;

// ── MÉDIAS (docs/rules/medias-resto.md, 29 août 2026) ──────────────────────
/** Durée maximale d'une vidéo (présentation ou story), en secondes. */
export const VIDEO_DUREE_MAX_S = 10;
/** Taille maximale d'un fichier vidéo accepté (dur, aussi dans storage.rules). */
export const VIDEO_TAILLE_MAX_OCTETS = 10 * 1024 * 1024;
/** Formats vidéo acceptés - PAS de QuickTime/HEVC (illisible hors Apple). */
export const VIDEO_TYPES_ACCEPTES = ['video/mp4', 'video/webm'] as const;
/** Taille maximale d'une image téléversée (logo/bannière, déjà réduite en WebP). */
export const IMAGE_TAILLE_MAX_OCTETS = 2 * 1024 * 1024;

// ── CADENCEMENT du service (docs/rules/pacing.md, 31 août 2026) ────────────
/** Unité du cadencement : un créneau = 30 minutes, comme partout ailleurs. */
export const PACING_PAS_MINUTES = 30;
/** Créneaux alternatifs proposés dans le push de refus (« Essayez 19h30 ou 21h00 »). */
export const PACING_ALTERNATIVES_MAX = 2;
/**
 * Pré-remplissage du champ « Couverts max par demi-heure » quand le
 * restaurateur ACTIVE le cadencement sans chiffre déjà saisi - pour qu'il
 * parte d'un ordre de grandeur plutôt que d'un champ vide. PROVISOIRE
 * (décision ouverte n°1 de docs/rules/pacing.md : un calcul depuis la
 * capacité réelle du service est proposé) ; sans effet sur un resto qui n'a
 * jamais touché le réglage.
 */
export const PACING_MAX_DEFAUT = 20;

// ── NOMBRE DE CONVIVES MAX par réservation (docs/rules/parametres-resto.md,
//    6 septembre 2026) - accès par maxConvives() (convives.ts) uniquement. ──
/**
 * Défaut d'un resto qui n'a rien réglé : 8 convives. Au-delà, l'app mobile
 * invite le client à appeler le restaurant - un groupe plus grand se discute.
 */
export const MAX_CONVIVES_DEFAUT = 8;
/** Plancher du réglage (une réservation d'au moins une personne). */
export const MAX_CONVIVES_MIN = 1;
/**
 * Plafond du réglage - aligné sur la borne dure des Firestore Rules
 * (`couverts <= 20` sur les modifications de demandes).
 */
export const MAX_CONVIVES_MAX = 20;
